import { useEffect, useMemo, useState } from "react"
import { updateObjective } from "src/services/api/opportunities"
import { intl } from "src/internationalization/intl"
import { useOpportunitiesStore } from "src/store"
import { useSnackbarStore } from "src/store/snackbarStore"
import { Button, CircularProgress, Grid, Modal, TextInput, Typography } from "../../basics"
import { EditObjectiveModalProps } from "./types"

const toDateInputValue = (isoOrDate: string | undefined): string => {
    if (!isoOrDate?.trim()) return ""
    const raw = isoOrDate.trim()
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
    const parsed = new Date(raw)
    if (Number.isNaN(parsed.getTime())) return ""
    const y = parsed.getFullYear()
    const m = String(parsed.getMonth() + 1).padStart(2, "0")
    const d = String(parsed.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

/** Hoje no fuso local — o backend rejeita data de início futura, então não pode vir de UTC. */
const todayInputValue = (): string => {
    const now = new Date()
    const m = String(now.getMonth() + 1).padStart(2, "0")
    const d = String(now.getDate()).padStart(2, "0")
    return `${now.getFullYear()}-${m}-${d}`
}

/** Janela válida: início no passado (ou hoje) e fim posterior ao início — espelha a validação do backend. */
const isValidWindow = (startInput: string, endInput: string): boolean => {
    const start = startInput.trim()
    const end = endInput.trim()
    if (!start || !end) return false
    if (start > todayInputValue()) return false
    return end > start
}

const EditObjectiveModal = ({
    objectiveId,
    title: initialTitle,
    createdDate,
    endDate,
    open,
    ...props
}: EditObjectiveModalProps) => {
    const [title, setTitle] = useState(initialTitle)
    const [startDateInput, setStartDateInput] = useState("")
    const [dueDateInput, setDueDateInput] = useState("")
    const [loading, setLoading] = useState(false)
    const triggerRefresh = useOpportunitiesStore((s: any) => s.triggerRefresh)
    const showSnackbar = useSnackbarStore((s) => s.showSnackbar)

    const initialStart = useMemo(() => toDateInputValue(createdDate), [createdDate])
    const initialDue = useMemo(() => toDateInputValue(endDate), [endDate])

    useEffect(() => {
        if (!open) return
        setTitle(initialTitle)
        setStartDateInput(initialStart)
        setDueDateInput(initialDue)
    }, [open, initialTitle, initialStart, initialDue])

    const canSave = title.trim().length > 0 && isValidWindow(startDateInput, dueDateInput)

    const confirmSave = async () => {
        if (!canSave) return
        try {
            setLoading(true)
            await updateObjective(objectiveId, {
                title: title.trim(),
                createdDate: startDateInput.trim(),
                endDate: dueDateInput.trim(),
            })
            showSnackbar(intl("editObjectiveSuccess"), "success")
            props.onClose()
            triggerRefresh()
        } catch {
            // apiFetch já mostra snackbar de erro
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal {...props} open={open} size="sm" title={intl("editObjectiveModalTitle")}>
            <Grid container direction="column" gap={2} sx={{ mt: 1 }}>
                <TextInput
                    label={intl("objectiveEditTitleLabel")}
                    value={title}
                    onChange={(e: any) => setTitle(e.target.value)}
                    fullWidth
                />
                <TextInput
                    label={intl("startDate")}
                    type="date"
                    value={startDateInput}
                    onChange={(e: any) => setStartDateInput(e.target.value)}
                    fullWidth
                    inputProps={{ max: todayInputValue() }}
                />
                <TextInput
                    label={intl("dueDate")}
                    type="date"
                    value={dueDateInput}
                    onChange={(e: any) => setDueDateInput(e.target.value)}
                    fullWidth
                    inputProps={{ min: startDateInput || undefined }}
                />
                <Typography variant="caption" color="quaternary">
                    {intl("objectiveEditWindowHint")}
                </Typography>
                <Grid container gap={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="outlined" color="neutral" onClick={props.onClose} disabled={loading}>
                        <Typography>{intl("cancel")}</Typography>
                    </Button>
                    <Button variant="contained" onClick={confirmSave} disabled={loading || !canSave}>
                        {loading ? (
                            <Grid container alignItems="center" gap={1}>
                                <CircularProgress size={16} color="primary" />
                                <Typography>{intl("saving")}</Typography>
                            </Grid>
                        ) : (
                            <Typography>{intl("save")}</Typography>
                        )}
                    </Button>
                </Grid>
            </Grid>
        </Modal>
    )
}

export { EditObjectiveModal }
